<template>
  <div
    class="w-full rounded-[16px] p-[12px] border-[#C3C2C1] border-[1px] border-solid"
    :class="hasPast ? 'bg-[#ECEBEB]' : 'bg-ivory'"
  >
    <div class="flex items-stretch gap-4 md:gap-6">
      <div class="flex flex-col items-center w-[70px] md:w-[90px] lg:w-[110px] my-auto">
        <EventDateInfo :has-past="hasPast" :start-date="startDate" :end-date="endDate" />
      </div>

      <div class="flex-1 min-w-0 border-l border-[#C3C2C1] pl-[12px] md:pl-[16px] lg:pl-[24px]">
        <div
          class="text-[1rem] md:text-[1.125rem] lg:text-[1.25rem] font-inter font-extrabold -tracking-[0.16px] leading-[1.5rem] md:leading-[1.75rem] lg:leading-[2rem] mb-1"
          :class="hasPast ? pastClass : ''"
        >
          {{ title }}
        </div>

        <div
          v-if="description"
          class="font-normal text-[1rem] md:text-[1.125rem] lg:text-[1.25rem] leading-[1.5rem] md:leading-[1.75rem] lg:leading-[2rem] mt-2"
          :class="hasPast ? pastClass : ''"
        >
          <p v-for="(line, index) in description.split('\n')" :key="index" class="min-h-[24px]">
            {{ line }}
          </p>
        </div>

        <EventWhenWhere :has-past="hasPast" :event-time-location="eventTimeLocation" />
        <div class="flex items-center justify-between min-w-0">
          <EventLabels :has-past="hasPast" :tags="tags" />
        </div>
        <button
          class="flex items-center text-gray-700 font-bold font-inter text-[#21201E] text-[0.875rem] md:text-[1rem] lg:text-[1.125rem] leading-[1.25rem] md:leading-[1.5rem] mt-2 ml-auto"
          :class="hasPast ? pastClass : ''"
        >
          <div
            v-if="link"
            class="flex items-center"
            @click="learnMore(link)"
            :class="hasPast ? pastClass : ''"
          >
            <span class="pr-[8px]">{{ buttonText ? buttonText : 'Learn More' }}</span>
            <img
              src="/assets/icons/milestones/chevron-right-white.svg"
              class="w-[16px] h-[16px] rounded-full bg-black"
              :class="hasPast ? 'opacity-50' : ''"
            />
          </div>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import EventDateInfo from '@/components/Calendar/Events/EventDateInfo.vue';
import EventWhenWhere from '@/components/Calendar/Events/EventWhenWhere.vue';
import EventLabels from '@/components/Calendar/Events/EventLabels.vue';
import dayjs from 'dayjs';
import { openUrl } from '@/utils/browser-helper';
import { trackEventViewed } from '@/utils/analytics';

export default {
  components: {
    EventDateInfo,
    EventWhenWhere,
    EventLabels,
  },
  data() {
    return {
      pastClass: 'text-[#7C7B78]',
    };
  },
  props: {
    title: {
      type: String,
      required: true,
      default: '',
    },
    description: {
      type: String,
      default: null,
    },
    eventTimeLocation: {
      type: Array,
      default: () => [],
    },
    link: {
      type: String,
      default: '',
    },
    buttonText: {
      type: String,
      required: false,
      default: 'Learn More',
    },
    isExternal: {
      type: Boolean,
      default: false,
    },
    dataEventTracking: {
      type: String,
      required: false,
    },
    startDate: {
      type: String,
      required: true,
      default: '',
    },
    endDate: {
      type: String,
      required: true,
      default: '',
    },
    tags: {
      type: Array,
      default: () => [],
    },
  },
  computed: {
    hasPast() {
      return dayjs().isAfter(this.endDate, 'day');
    },
  },
  methods: {
    getEventAnalyticsSlug() {
      if (this.dataEventTracking) return this.dataEventTracking;

      const linkSlug = this.link?.match(/\/event-page\/([^/?#]+)/)?.[1];
      if (linkSlug) return linkSlug;

      return [this.title, this.startDate]
        .filter(Boolean)
        .join('-')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
    },
    async learnMore(link) {
      const eventSlug = this.getEventAnalyticsSlug();
      trackEventViewed({
        eventId: this.dataEventTracking || eventSlug,
        slug: eventSlug,
        title: this.title,
        source: 'calendar-event-card',
      });
      if (this.isExternal) {
        await openUrl(
          { url: link },
          {
            title: this.title,
            source: 'calendar-event',
            page: 'Calendar',
          },
        );
      } else {
        this.$router.push(link);
      }
    },
  },
};
</script>
